import {
  ALL_AROUND_MIN_DISCIPLINES,
  ALL_AROUND_MIN_POINTS,
} from 'constants/allAround';

/**
 * Combines several per-discipline leaderboards into a single all around
 * standing: one row per athlete, with their points summed across the
 * disciplines they placed in.
 *
 * Takes an array of leaderboards, each an array of rows as the leaders endpoint
 * returns them. Rows are matched on ERAUID.
 *
 * Two rules decide who appears, both carried over from the leaderboard on the
 * WCRA WordPress site so the two agree:
 *
 *   - an athlete must have placed in more than one discipline. Someone with a
 *     single discipline has no all around standing, however well they did.
 *   - the summed total must clear a points floor, which keeps rows that scored
 *     a fraction of a point out of the table.
 *
 * The identity fields are taken from the first row seen for that athlete. They
 * are the same in every discipline, and the table needs them to render.
 */
export const mergeAllAroundLeaderboards = (leaderboards = []) => {
  const byAthlete = new Map();

  leaderboards.forEach((leaderboard) => {
    (leaderboard || []).forEach((row) => {
      if (!row || row.ERAUID === undefined || row.ERAUID === null) return;

      const key = String(row.ERAUID);
      if (!byAthlete.has(key)) {
        byAthlete.set(key, {
          ERAUID: row.ERAUID,
          FullName: row.FullName,
          userId: row.userId,
          youthUserId: row.youthUserId,
          disciplines: new Set(),
          points: 0,
        });
      }

      const athlete = byAthlete.get(key);

      // Disciplines are counted as a set of ids rather than a tally of rows,
      // because the boards being merged now span two competition levels. One
      // athlete appearing in the same discipline at both would otherwise count
      // as two towards the minimum and look like a spread she does not have.
      // No athlete in W27 holds two levels in one discipline, so this changes
      // no current total - it just stops the rule depending on that staying
      // true.
      athlete.disciplines.add(
        row.disciplineId === undefined || row.disciplineId === null
          ? `unknown-${athlete.disciplines.size}`
          : String(row.disciplineId),
      );
      athlete.points += Number(row.points) || 0;

      // A row can be missing a name if the athlete record was incomplete when
      // that discipline was scored. Take the first value that is actually set.
      if (!athlete.FullName && row.FullName) athlete.FullName = row.FullName;
      if (!athlete.userId && row.userId) athlete.userId = row.userId;
      if (!athlete.youthUserId && row.youthUserId) {
        athlete.youthUserId = row.youthUserId;
      }
    });
  });

  const ranked = [...byAthlete.values()]
    .map(({ disciplines, ...athlete }) => ({
      ...athlete,
      disciplineCount: disciplines.size,
    }))
    .filter(
      (athlete) =>
        athlete.disciplineCount >= ALL_AROUND_MIN_DISCIPLINES &&
        athlete.points >= ALL_AROUND_MIN_POINTS,
    )
    .sort((a, b) => b.points - a.points);

  // The table renders contestant.position rather than the row index, so the
  // merged rows have to carry it the way the API's own rows do.
  //
  // Numbered straight down, with no shared places for equal totals. That is
  // what the leaders endpoint itself returns - its positions run 1, 2, 3 even
  // where the points are identical - and the table uses position as its React
  // key, so repeating one would collide.
  ranked.forEach((athlete, index) => {
    athlete.position = index + 1;
  });

  return ranked;
};
